// React
import { useState, useRef, useCallback, ChangeEvent } from "react"

// Dates
import { DateTime } from "@/lib/dates"
import { dateFormat } from "@/constants/constants"

// PDF
import { PDFViewWrapper } from "@/lib/pdf"
import { generatePDF, getPdfFileName } from "@/lib/pdf.utils"

// Translations
import { useTrans } from "@/i18n"

// UI
import { CardBody, CardWrapper } from "@/components/Card"
import { Input } from "@/components/form-controls"
import { ErrorBoundaryWithErrorState } from "@/components/errors/ErrorBoundary"
import { DownloadSelector } from "@/components/DownloadSelector"
import { Disclaimer } from "@/components/Disclaimer"

// Specific UI
import { PayoutsTablePrint } from "./_components/PayoutsTablePrint"
import { PayoutsTableWeb } from "./_components/PayoutsTableWeb"

// Types
import { DownloadableFileType } from "@/components/DownloadSelector/types"

/**
 * All Payouts
 * @returns
 */
export const AllPayments = () => {
	// State
	const [isPdfLoading, setIsPdfLoading] = useState(false)
	const [shouldFetchPdfData, setShouldFetchPdfData] = useState(false)
	const [transactionId, setTransactionId] = useState<string | undefined>(
		undefined,
	)

	// Translations
	const t = useTrans(["project", "payments"])

	// PDF
	const pdfViewRef = useRef(null)
	const PDF_TITLE = t("project:revenue.all.export.title", {
		date: DateTime.local().toFormat(dateFormat),
	})
	const PDF_VIEW_WIDTH = 1920 // // Width that the table renders optimally at
	async function handlePdfDownload() {
		if (pdfViewRef.current) {
			return generatePDF(pdfViewRef.current, {
				filename: getPdfFileName(PDF_TITLE),
				overrideWidth: PDF_VIEW_WIDTH,
				compression: "FAST",
			})
		}
	}

	/**
	 * handleDownload
	 * Triggers the PDF data fetch. PDF generation happens via onDataReady callback.
	 * @param type
	 */
	function handleDownload(type: DownloadableFileType) {
		if (type === DownloadableFileType.Pdf) {
			setIsPdfLoading(true)
			setShouldFetchPdfData(true)
		}
	}

	/**
	 * handlePdfDataReady
	 * Called when PayoutsTablePrint has finished loading data.
	 * Triggers the actual PDF generation.
	 */
	const handlePdfDataReady = useCallback(() => {
		// Only generate PDF if we're in the loading state (user requested PDF)
		if (isPdfLoading) {
			void handlePdfDownload().then(() => {
				setIsPdfLoading(false)
				return
			})
		}
	}, [isPdfLoading])

	// Return
	return (
		<>
			<title>
				{t("common:web.title", {
					page: t("project:revenue.all.title"),
				})}
			</title>

			<CardWrapper className="mt-5">
				<CardBody>
					{/* filters */}
					<div className="align-end mb-4 flex flex-col justify-between gap-2 md:mb-6 lg:flex-row lg:gap-0">
						<div className="space-between flex w-full flex-wrap gap-x-4 gap-y-2">
							<div className="ml-auto">
								{/** Invisible Print view */}
								<PDFViewWrapper
									ref={pdfViewRef}
									title={PDF_TITLE}
									width={PDF_VIEW_WIDTH}
								>
									<PayoutsTablePrint
										enabled={shouldFetchPdfData}
										onDataReady={handlePdfDataReady}
									/>

									{/** Legal disclaoimer */}
									<Disclaimer
										title={t(
											"investments:fiscal.overview.legal-disclaimer.title",
										)}
										message={t(
											"investments:fiscal.overview.legal-disclaimer.copy",
										)}
									/>
								</PDFViewWrapper>

								<div className="flex gap-2">
									<Input
										placeholder={t(
											"payments:info_list.transaction_id.title",
										)}
										value={transactionId}
										onChange={(
											event: ChangeEvent<HTMLInputElement>,
										) =>
											setTransactionId(event.target.value)
										}
									/>
									<DownloadSelector
										options={[
											{
												name: t(
													"common:button.download.pdf",
												),
												value: DownloadableFileType.Pdf,
											},
										]}
										onDownload={handleDownload}
										disabled={isPdfLoading}
									/>
								</div>
							</div>
						</div>
					</div>

					{/** Show table for web */}
					<ErrorBoundaryWithErrorState errorBoundaryClassName="mt-5">
						<PayoutsTableWeb transactionId={transactionId} />
					</ErrorBoundaryWithErrorState>
				</CardBody>

				{/** Legal disclaoimer */}
				<Disclaimer
					title={t(
						"investments:fiscal.overview.legal-disclaimer.title",
					)}
					message={t(
						"investments:fiscal.overview.legal-disclaimer.copy",
					)}
				/>
			</CardWrapper>
		</>
	)
}
